import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { arriveMotion } from '@/src/renderer/lib/motion/arriveMotion'
import { whenCalm } from '@/src/renderer/lib/motion/whenCalm'
import { ListToolbar } from '../ListToolbar'
import { useListToolbarPosition } from '../ListToolbar/hooks/useListToolbarPosition'
import { setListToolbarPosition, type ListToolbarPosition } from '../ListToolbar/utils/listToolbarPosition'
import { resolveListToolbarVariant } from '../ListToolbar/utils/resolveListToolbarVariant'
import { useMagnetDrag, type MagnetDragGeometry } from '../../hooks/useMagnetDrag'
import { selectionFit } from '../../utils/selectionCommands'
import type { ListToolbarDockProps } from './ListToolbarDock.types'
import './ListToolbarDock.scss'

/** Seitlicher Abstand der Pille zur Spaltenkante — wie der untere (ListToolbarDock.scss). */
export const LIST_TOOLBAR_GUTTER = 24

/**
 * Wie viel unten in der Liste die Pille belegt: 24px Abstand, 48px Pille,
 * 16px Luft. Der Scroller hält so viel frei (`$list-bottom-gap` in
 * ProjectPage.scss), damit die letzte Zeile und der Ersteller nicht hinter
 * ihr liegen.
 */
export const LIST_TOOLBAR_CLEARANCE = 88

/**
 * Breite von `short` im Figma (5550:66845). Fest statt gemessen: Gemessen
 * werden könnte sie nur, während sie steht — und gerade dann, wenn sie nicht
 * passt, steht `compact`.
 */
export const LIST_TOOLBAR_SHORT_WIDTH = 314

const POSITIONS: ListToolbarPosition[] = ['left', 'center', 'right']

/** Wie weit sie beim Anlegen abtaucht: Pille 48 + Abstand 24 + 24 Luft — ganz aus dem Bild. */
const RETRACT_DISTANCE = 96
/** Herauf mit Schwung (Prototyp `tools/list-toolbar-retract-prototype`, Variante D). */
const RISE_DURATION = 0.45

/**
 * Die **Listen-Werkzeug-Pille** an ihrem Platz unten in der Aufgabenliste:
 * links, mittig oder rechts, gezogen am **Griff**. Am Rand ruht sie
 * eingeklappt (`justAdd`) und fährt beim Überfahren aus. Beim Ziehen
 * erscheinen die drei Ablageflächen, sonst sind sie unsichtbar.
 *
 * Wohin angelegt wird, entscheidet die Seite — der Dock reicht nur die Klicks durch.
 * Solange angelegt wird (`retracted`), taucht sie nach unten ab und kommt danach
 * mit Schwung wieder herauf.
 *
 * @example
 * <ListToolbarDock isActive={isActive} columnRef={listColumnRef} canvasAvailable canvasOpen={canvasIsOpen}
 *   onAddTask={…} onAddSection={…} onPlaceNote={…} onPlaceMemo={…} />
 */
export const ListToolbarDock: React.FC<ListToolbarDockProps> = ({
  isActive, columnRef, canvasAvailable, canvasOpen, sectionsAllowed = true, disabled = false, retracted = false,
  position: fixedPosition, onAddTask, onAddSection, onPlaceNote, onPlaceMemo, onNotePointerDown, onMemoPointerDown,
  onAddTaskPointerDown, addTaskDragging = false, onAddSectionPointerDown, addSectionDragging = false, renderSelection, className,
}) => {
  const storedPosition = useListToolbarPosition()
  const position: ListToolbarPosition = fixedPosition ?? storedPosition
  const [expanded, setExpanded] = useState(false)

  /*
    **Abtauchen beim Anlegen.** Hinab sofort, herauf erst, wenn es ruhig ist:
    Im Moment des „fertig“ läuft die neue Zeile ein, und eine Bewegung, die
    mitten darin startet, ruckelt (`whenCalm`).

    Abgetaucht meldet die Pille kein Verlassen mehr — der Zeiger, der eben
    geklickt hat, stand auf ihr. Ohne Rücksetzen käme sie am Rand ausgefahren
    zurück.
  */
  const [down, setDown] = useState(retracted)
  // Weg oder noch unterwegs nach oben — erst wenn sie steht, wieder `false`.
  const [away, setAway] = useState(retracted)
  const [prevRetracted, setPrevRetracted] = useState(retracted)
  if (retracted !== prevRetracted) {
    setPrevRetracted(retracted)
    if (retracted) {
      setDown(true)
      setAway(true)
      setExpanded(false)
    }
  }
  useEffect(() => {
    if (retracted || !down) return
    return whenCalm(() => setDown(false))
  }, [retracted, down])

  const reducedMotion = useReducedMotion() ?? false
  const retractMotion = useMemo(
    () => arriveMotion({ distance: RETRACT_DISTANCE, duration: RISE_DURATION, delay: 0, overshoot: true, reduced: reducedMotion }),
    [reducedMotion],
  )

  /*
    Ob `short` hineinpasst. Gemessen nur im sichtbaren Tab und nur bei echter
    Breite: Ein versteckter Tab liefert 0 (CLAUDE.md, TabStack), und die Pille
    spränge sonst beim Zurückkehren erst auf `compact`.

    `useEffect`, nicht `useLayoutEffect`: Die Spalte ist ein Vorfahr, und
    deren Ref hängt React erst NACH den Layout-Effekten der Kinder an.
  */
  // Die Breite selbst, nicht nur „passt `short`“: Die Befehle der Auswahl zählen daran ihre Plätze (#291).
  const [columnWidth, setColumnWidth] = useState<number | null>(null)
  const roomForShort = columnWidth == null || columnWidth >= LIST_TOOLBAR_SHORT_WIDTH + 2 * LIST_TOOLBAR_GUTTER
  useEffect(() => {
    if (!isActive) return
    const column = columnRef.current
    if (!column) return
    const measure = () => {
      const width = column.getBoundingClientRect().width
      if (width === 0) return
      setColumnWidth(Math.round(width))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(column)
    return () => observer.disconnect()
  }, [isActive, columnRef])

  const pillRef = useRef<HTMLDivElement>(null)
  const dockRef = useRef<HTMLDivElement>(null)

  /*
    Wo die Pille an jeder Stelle stünde: links und rechts bündig im Abstand
    zur Kante, mittig zentriert. Gemessen beim Zugbeginn — dann steht sie
    ausgefahren, denn der Zeiger ist auf ihr.
  */
  const measureDrag = useCallback((): MagnetDragGeometry<ListToolbarPosition> | null => {
    const pill = pillRef.current
    const dock = dockRef.current
    if (!pill || !dock) return null
    const pillRect = pill.getBoundingClientRect()
    const frame = dock.getBoundingClientRect()
    if (frame.width === 0) return null
    // Die Ablageflächen haben keinen Inhalt — sie bekommen die Breite der Pille.
    dock.style.setProperty('--list-toolbar-zone-width', `${pillRect.width}px`)
    const at = (left: number) => new DOMRect(left, pillRect.top, pillRect.width, pillRect.height)
    return {
      pill: pillRect,
      targets: {
        left: at(frame.left + LIST_TOOLBAR_GUTTER),
        center: at(frame.left + (frame.width - pillRect.width) / 2),
        right: at(frame.right - LIST_TOOLBAR_GUTTER - pillRect.width),
      },
    }
  }, [])

  const handleDrop = useCallback((next: ListToolbarPosition) => {
    // Gelandet: am Rand eingeklappt, bis der Zeiger wieder darüberfährt.
    setExpanded(false)
    setListToolbarPosition(next)
  }, [])

  const drag = useMagnetDrag<ListToolbarPosition>({
    current: position,
    pillRef,
    measure: measureDrag,
    onChange: handleDrop,
  })
  const dragging = drag.phase !== 'idle'

  const variant = resolveListToolbarVariant({
    canvasAvailable,
    canvasOpen,
    roomForShort,
    position,
    // Während des Zugs bleibt sie ausgefahren — sonst schrumpfte sie unter der Hand. Ebenso, solange
    // „Neue Aufgabe“ herausgezogen wird (#301): Die Pille taucht dabei ab (`retracted`) und soll das in ihrer
    // Form tun, nicht vorher einklappen.
    expanded: expanded || dragging || addTaskDragging || addSectionDragging,
    sectionsAllowed,
    selecting: Boolean(renderSelection),
  })
  const fit = renderSelection && selectionFit({
    columnWidth: columnWidth ?? Infinity,
    hasSection: variant !== 'addOnly' && variant !== 'justAdd',
    taskLabel: variant === 'short' || variant === 'addOnly',
  })
  const selection = fit ? renderSelection(fit.slots) : undefined

  const rootClass = [
    'list-toolbar-dock',
    `list-toolbar-dock--${position}`,
    dragging && 'list-toolbar-dock--dragging',
    drag.phase === 'dragging' && 'list-toolbar-dock--lifted',
    away && 'list-toolbar-dock--away',
    className,
  ].filter(Boolean).join(' ')

  return (
    <div ref={dockRef} className={rootClass}>
      {dragging && POSITIONS.map(spot => (
        <div
          key={spot}
          className={`list-toolbar-dock__zone list-toolbar-dock__zone--${spot}${drag.nearest === spot ? ' list-toolbar-dock__zone--armed' : ''}`}
          aria-hidden="true"
        />
      ))}
      <motion.div
        className="list-toolbar-dock__slot"
        initial={false}
        animate={down ? retractMotion.exit : retractMotion.animate}
        onAnimationComplete={() => {
          if (!down) setAway(false)
        }}
        inert={down}
        aria-hidden={down || undefined}
        onPointerEnter={() => setExpanded(true)}
        onPointerLeave={() => setExpanded(false)}
      >
        <ListToolbar
          ref={pillRef}
          variant={variant}
          onAddTask={onAddTask}
          onAddSection={onAddSection}
          onPlaceNote={onPlaceNote}
          onPlaceMemo={onPlaceMemo}
          onNotePointerDown={onNotePointerDown}
          onMemoPointerDown={onMemoPointerDown}
          onAddTaskPointerDown={onAddTaskPointerDown}
          addTaskDragging={addTaskDragging}
          onAddSectionPointerDown={onAddSectionPointerDown}
          addSectionDragging={addSectionDragging}
          onGripPointerDown={drag.gripPointerDown}
          gripPressed={drag.phase === 'dragging'}
          disabled={disabled}
          selection={selection}
          taskLabel={fit ? fit.taskLabel : true}
        />
      </motion.div>
    </div>
  )
}
